import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDeepCompareEffect, useIsomorphicLayoutEffect } from 'ahooks';
import type { Swiper as SwiperCore } from 'swiper';
import {
    Button,
    Carousel,
    pxToRem,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import {
    ArrowLeftSlim,
    ArrowRightSlim,
    DownloadLine,
    HeartFill,
    HeartLine,
    PlayFill,
    SearchLine,
} from '@archipro-design/icons';
import { useTracker } from '@archipro-website/tracker';
import ImageSlide from './ImageSlide';
import VideoSlide from './VideoSlide';
import * as S from './ProductGallery.style';
import {
    PRODUCT_GALLERY_SIZE,
    PRODUCT_THUMB_SIZE,
} from './ProductGallery.style';
import PhotoViewerModal from './PhotoViewerModal';
import type {
    LibraryDiagram,
    LibraryImage,
    LibraryVideo,
} from '@modules/product/type/product-gallery-types';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import { FollowSelector } from '~/modules/moodboards/components';
import useGalleryVariantSelect from '@modules/product/hook/use-gallery-variant-select';
import getGallerySlides from '~/modules/product/util/get-gallery-slides';
import ProductGalleryThumbs from './ProductGalleryThumbs';
import { useCanDownloadImages } from '@modules/user';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import type { AwardProps } from '@modules/page-builder/component/detail/banner-awards/types';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { useFeature } from '@modules/root';
import { encodeDownloadLink } from '~/modules/page-builder/component/detail/detail-single-image-shell/HoverButtonsImage';
import { hex2rgba } from '~/utils/colorHelper';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import {
    applyLightboxSlideToGallery,
    applySwiperIndexToGallery,
} from '~/modules/product/util/sync-gallery-lightbox-index';

/** Width in pixels of the left/right paddle zones in the image overlay. */
const PADDLE_ZONE_WIDTH = 80;

type CursorZone = 'left' | 'center' | 'right';

interface ProductGalleryData {
    ID: number;
    LibraryImages: LibraryImage[];
    LibraryVideos: LibraryVideo[];
    DiagramImages: LibraryDiagram[];
    Awards?: AwardProps[];
}

interface ProductGalleryProps {
    data: ProductGalleryData;
    title?: string;
    loop?: boolean;
    /** Optional CTA configuration for the photo viewer modal */
    cta?: {
        /** External website URL with tracking params already applied */
        websiteUrl?: string;
        /** Additional tracking params for website link tracking */
        websiteTrackingParams?: Record<string, string | number | undefined>;
        /** Callback to open enquiry modal */
        onEnquire?: () => void;
        /** Label for enquiry button */
        enquiryLabel?: string;
    };
}

const ProductGallery = ({ data, title, loop, cta }: ProductGalleryProps) => {
    const { ID, LibraryImages, LibraryVideos, DiagramImages } = data;
    const [gallery, setGallery] = useState<SwiperCore | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    /** Latest lightbox open state for the Swiper slideChange listener. */
    const modalOpenRef = useRef(modalOpen);
    modalOpenRef.current = modalOpen;
    const tracker = useTracker();
    const enableAwardBadges = useFeature('award_badges');

    const { css } = useStyles({ swiperInitialised: !!gallery });

    const canDownloadImages = useCanDownloadImages();

    const stack = useMemo(
        () => getGallerySlides({ LibraryImages, LibraryVideos, DiagramImages }),
        [LibraryImages, LibraryVideos, DiagramImages],
    );

    /** Stable key that changes when slide composition changes, forcing Swiper to remount with correct DOM order. */
    const carouselKey = useMemo(
        () => stack.map((s) => s.ID).join('-'),
        [stack],
    );

    const { slideIndex } = useGalleryVariantSelect(stack);

    const slideToIndex = useCallback(
        (index: number, speed: number = 400) => {
            if (loop) {
                gallery?.slideToLoop(index, speed, false);
            } else {
                gallery?.slideTo(index, speed, false);
            }
        },
        [loop, gallery],
    );
    /** Opens the lightbox and records the interaction. */
    const openPhotoViewer = useCallback(() => {
        tracker.log('PhotoViewerTrigger', {
            url: new URL(window.location.href),
            data: {
                LibraryItemID: ID || 0,
            },
        });
        setModalOpen(true);
    }, [tracker, ID]);

    const { siteVariables } = useTheme();
    const [activeIndex, setActiveIndex] = useState(slideIndex);
    const [cursorPos, setCursorPos] = useState<{
        x: number;
        y: number;
        overlayWidth: number;
    } | null>(null);
    const prevProductIdRef = useRef(data.ID);
    const { isInDesignBoard } = useHandleFollow();

    /** Returns pin config for a slide. LibraryImage uses PhotoPin; DiagramImage/Video use ProductPin. */
    const getSlidePinConfig = useCallback(
        (slide: (typeof stack)[number]) => {
            if (slide.__typename === 'LibraryImage') {
                return {
                    pinId: slide.ID,
                    pinType: 'PhotoPin' as const,
                    libraryItemId: ID,
                };
            }
            return {
                pinId: ID,
                pinType: 'ProductPin' as const,
                libraryItemId: undefined,
            };
        },
        [ID],
    );

    const activePinConfig = useMemo(() => {
        const slide = stack[activeIndex];
        if (!slide) {
            return {
                pinId: ID,
                pinType: 'ProductPin' as const,
                libraryItemId: undefined as number | undefined,
            };
        }
        return getSlidePinConfig(slide);
    }, [activeIndex, stack, ID, getSlidePinConfig]);

    const slides = useMemo(
        () =>
            stack.map((slide) => {
                const isVideo = isLibraryVideo(slide);
                const imageThumbnailSrc = !isVideo
                    ? slide.__typename === 'DiagramImage'
                        ? slide.Thumbnail.Desktop
                        : slide.LibraryImageFile?.ThumbnailBM180
                    : slide.LibraryImage?.LibraryImageFile?.VideoCover
                          .Desktop || slide.ThumbnailOfficial;
                const isMultiplied =
                    slide.__typename === 'LibraryImage'
                        ? slide.IsMultiplied
                        : false;
                return {
                    key: `${slide.ID}-ImageSlide`,
                    id: slide.ID,
                    content: !isVideo ? (
                        <ImageSlide
                            libraryImage={
                                slide.LibraryImageFile?.DetailPageSquare
                                    .Desktop ?? ''
                            }
                            isMultiplied={isMultiplied}
                            onClick={openPhotoViewer}
                            height={PRODUCT_GALLERY_SIZE}
                            width={PRODUCT_GALLERY_SIZE}
                            sizes={getImagesSizes({
                                desktop: PRODUCT_GALLERY_SIZE,
                                mobile: 375,
                            })}
                            loadingValue={'eager'}
                            alt={
                                slide.AltText || `${title} gallery detail image`
                            }
                        />
                    ) : (
                        <VideoSlide libraryVideo={slide} />
                    ),
                    thumbnail: (
                        <ImageSlide
                            libraryImage={imageThumbnailSrc ?? ''}
                            isMultiplied={isMultiplied}
                            width={PRODUCT_THUMB_SIZE}
                            height={PRODUCT_THUMB_SIZE}
                            loadingValue={'eager'}
                            alt={
                                (slide.__typename !== 'LibraryVideo' &&
                                    slide.AltText) ||
                                `${title} gallery detail image`
                            }
                            sizes={getImagesSizes(PRODUCT_THUMB_SIZE)}
                            isVideo={isVideo}
                        />
                    ),
                };
            }),
        [stack, title, openPhotoViewer],
    );

    const isVideo = isLibraryVideo(stack[activeIndex]);
    const downloadLink = encodeDownloadLink(
        !isVideo && canDownloadImages
            ? (stack[activeIndex] as LibraryImage)?.DownloadLink || ''
            : '',
    );
    const hasPaddles = slides.length >= 2;

    const cursorZone: CursorZone = useMemo(() => {
        if (!cursorPos || !hasPaddles) return 'center';
        if (cursorPos.x < PADDLE_ZONE_WIDTH) return 'left';
        if (cursorPos.x > cursorPos.overlayWidth - PADDLE_ZONE_WIDTH)
            return 'right';
        return 'center';
    }, [cursorPos, hasPaddles]);

    const cursorIcon =
        cursorZone === 'left' ? (
            <ArrowLeftSlim />
        ) : cursorZone === 'right' ? (
            <ArrowRightSlim />
        ) : (
            <SearchLine />
        );

    useEffect(() => {
        setActiveIndex(slideIndex);
        slideToIndex(slideIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slideIndex]);

    useDeepCompareEffect(() => {
        gallery?.update();
        const isNewProduct = data.ID !== prevProductIdRef.current;
        prevProductIdRef.current = data.ID;

        if (isNewProduct) {
            setActiveIndex(0);
            slideToIndex(0, 0);
        }
    }, [data]);

    /**
     * Keep activeIndex aligned with Swiper when the user navigates the carousel.
     * Skip while the lightbox is open — the modal owns activeIndex then.
     */
    useIsomorphicLayoutEffect(() => {
        const current = gallery;
        if (!current) return;

        const onSlideChange = (swiper: SwiperCore) => {
            applySwiperIndexToGallery(
                swiper.realIndex,
                modalOpenRef.current,
                setActiveIndex,
            );
        };
        current.on('slideChange', onSlideChange);
        return () => {
            current.off('slideChange', onSlideChange);
        };
    }, [gallery]);

    /** Sync gallery + thumbs when the lightbox focused photo changes. */
    const handleModalSlideChange = useCallback(
        (stackIndex: number) => {
            applyLightboxSlideToGallery(stackIndex, {
                setActiveIndex,
                slideToIndex,
            });
        },
        [slideToIndex],
    );

    return (
        <div className={css(S.ProductGallerySticky)}>
            <div className={css(S.ProductGalleryContainer)}>
                <Carousel
                    key={carouselKey}
                    items={slides}
                    getSwiperApi={(swiper) => setGallery(swiper as SwiperCore)}
                    mixBlendMode="difference"
                    topCarouselActions={[
                        isVideo ? (
                            <Button
                                color="primary"
                                variant="outlined"
                                key="video-player-button-overlay"
                                size={44}
                                className={css(S.VideoButton)}
                                icon={<PlayFill />}
                                design={{ position: 'absolute' }}
                            />
                        ) : (
                            <div
                                className={css(S.ViewImageOverlay)}
                                key="view-photo-overlay"
                                onClick={() => {
                                    if (cursorZone === 'left') {
                                        gallery?.slidePrev();
                                    } else if (cursorZone === 'right') {
                                        gallery?.slideNext();
                                    } else {
                                        openPhotoViewer();
                                    }
                                }}
                                onMouseMove={(e) => {
                                    const target = e.target as HTMLElement;
                                    if (
                                        target.closest(
                                            'button, a, [data-no-magnifier]',
                                        )
                                    ) {
                                        setCursorPos(null);
                                        return;
                                    }
                                    const rect =
                                        e.currentTarget.getBoundingClientRect();
                                    setCursorPos({
                                        x: e.clientX - rect.left,
                                        y: e.clientY - rect.top,
                                        overlayWidth: rect.width,
                                    });
                                }}
                                onMouseLeave={() => setCursorPos(null)}
                            >
                                <div
                                    className="pointer-events-auto absolute top-4 right-4 z-1000 flex items-center gap-2"
                                    onMouseEnter={() => setCursorPos(null)}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Button
                                        color="primary"
                                        variant="text"
                                        icon={<DownloadLine />}
                                        as="a"
                                        href={downloadLink}
                                        target={'_blank'}
                                        rel="noopener"
                                        download
                                        size={16}
                                        className={css(S.DownloadButton)}
                                        aria-hidden={isVideo || !downloadLink}
                                        style={{
                                            borderRadius: '50%',
                                        }}
                                    />
                                    <FollowSelector
                                        trigger={
                                            <Button
                                                className={css(
                                                    S.FavouriteButtonInline,
                                                )}
                                                size={18}
                                                color="primary"
                                                variant="text"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                }}
                                                icon={
                                                    isInDesignBoard(
                                                        activePinConfig.pinId,
                                                        activePinConfig.pinType,
                                                    ) ? (
                                                        <HeartFill size="2x" />
                                                    ) : (
                                                        <HeartLine size="2x" />
                                                    )
                                                }
                                                variables={{
                                                    buttonRadius: '50%',
                                                    buttonPadding: pxToRem(0),
                                                    primaryNoBKGButtonTextColorNormal:
                                                        siteVariables.colors
                                                            .primitive.white,
                                                    primaryNoBKGButtonTextColorHover:
                                                        siteVariables.colors
                                                            .primitive.white,
                                                    primaryNoBKGButtonBackgroundColorNormal:
                                                        hex2rgba(
                                                            siteVariables.colors
                                                                .primitive
                                                                .black,
                                                            0.1,
                                                        ),
                                                    primaryNoBKGButtonBackgroundColorHover:
                                                        hex2rgba(
                                                            siteVariables.colors
                                                                .primitive
                                                                .black,
                                                            0.4,
                                                        ),
                                                }}
                                            />
                                        }
                                        pinId={activePinConfig.pinId}
                                        pinType={activePinConfig.pinType}
                                        libraryItemId={
                                            activePinConfig.libraryItemId
                                        }
                                    />
                                </div>
                                {cursorPos && (
                                    <Button
                                        color="primary"
                                        variant="text"
                                        icon={cursorIcon}
                                        className={css(S.CursorIcon)}
                                        variables={{
                                            buttonRadius: '0',
                                            primaryNoBKGButtonBackgroundColorHover:
                                                'transparent',
                                        }}
                                        style={{
                                            left: cursorPos.x,
                                            top: cursorPos.y,
                                        }}
                                    />
                                )}
                            </div>
                        ),
                    ]}
                    paddleNext={{
                        children: (
                            <ArrowRightSlim className={css(S.PaddleButton)} />
                        ),
                        variables: {
                            paddleHeight: pxToRem(160),
                            paddleBackgroundColor: 'rgba(242, 241, 240, 0.5)',
                        },
                    }}
                    paddlePrevious={{
                        children: (
                            <ArrowLeftSlim className={css(S.PaddleButton)} />
                        ),
                        variables: {
                            paddleHeight: pxToRem(160),
                            paddleBackgroundColor: 'rgba(242, 241, 240, 0.5)',
                        },
                    }}
                    className={css(S.Carousel)}
                    variables={{
                        paddleHeight: pxToRem(110),
                        paddleBackgroundColor: 'rgba(242, 241, 240, 0.5)',
                        containerWidth: pxToRem(PRODUCT_GALLERY_SIZE),
                        containerHeight: pxToRem(PRODUCT_GALLERY_SIZE),
                    }}
                    activeIndex={activeIndex}
                    defaultActiveIndex={activeIndex}
                    loop={loop}
                    hidePaddles={slides.length < 2}
                >
                    {enableAwardBadges &&
                        data.Awards &&
                        data.Awards.length > 0 && (
                            <BannerAwards
                                awards={data.Awards}
                                awardsPerSlide={5}
                            />
                        )}
                </Carousel>
                <ProductGalleryThumbs
                    navigation={slides}
                    activeGallerySlide={gallery ? activeIndex : 0}
                    swipeInitialised={!!gallery}
                    slideToIndex={slideToIndex}
                />
            </div>
            {modalOpen && (
                <PhotoViewerModal
                    key={ID}
                    stack={stack}
                    selectedSlide={activeIndex}
                    open={modalOpen}
                    toggle={() => setModalOpen((curr) => !curr)}
                    itemID={ID}
                    title={title}
                    cta={cta}
                    onSlideChange={handleModalSlideChange}
                />
            )}
        </div>
    );
};

export default ProductGallery;
